import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MdOutlineModeEditOutline } from "react-icons/md";
import { IoClose, IoInformationCircleOutline } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import { ActionIcon, Button, Modal, Switch, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import moment from 'moment';
import DeletePopover from "../../../components/buttons/DeletePopover";
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { menuRoutes } from '../../../router/menu';
import { ProductService } from "../../../services/product/productService";
import { ProductsApiResponse } from "../../../type/entities/product-type";
import { fixNumber } from '../../../utils/formatNumber';
import ProductsFilters from './ProductsFilters';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';

interface ProductsListProps {
    forcedProductTypeId?: string;
    forcedExcludedProductTypeId?: string;
    hideProductTypeFilter?: boolean;
}

type ProductImportMode = 'preview' | 'upsert' | 'create_only' | 'update_only';

type ProductImportPreview = {
    rowsWithId: number;
    rowsWithoutId: number;
    totalRows: number;
};

const columnOrder = [
    'id',
    'name',
    'productType',
    'code',
    'amount',
    'weeklyHours',
    'description',
    'complementsNumber',
    'active',
    'createdAt',
    'updatedAt',
];

const ProductsList: React.FC<ProductsListProps> = ({
    forcedProductTypeId,
    forcedExcludedProductTypeId,
    hideProductTypeFilter = false
}) => {

    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const service = new ProductService();
    const { userCan } = usePrivilege();
    const canExportProductsExcel = userCan('admin_products', 'products') && userCan('export_products', 'products');
    const canImportProductsExcel = userCan('admin_products', 'products') && userCan('create_products', 'products');

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isLoadingXLSX, setIsLoadingXLSX] = useState<boolean>(false);
    const [isLoadingXML, setIsLoadingXML] = useState<boolean>(false);
    const [isLoadingImport, setIsLoadingImport] = useState<boolean>(false);
    const [isLoadingImportTemplate, setIsLoadingImportTemplate] = useState<boolean>(false);
    const [showImportStartModal, setShowImportStartModal] = useState<boolean>(false);
    const [showImportModeModal, setShowImportModeModal] = useState<boolean>(false);
    const [pendingImportFile, setPendingImportFile] = useState<File | null>(null);
    const [importPreview, setImportPreview] = useState<ProductImportPreview | null>(null);
    const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
    const importFileInputRef = useRef<HTMLInputElement | null>(null);
    const previousForcedProductTypeIdRef = useRef<string | undefined>(forcedProductTypeId);
    const previousForcedExcludedProductTypeIdRef = useRef<string | undefined>(forcedExcludedProductTypeId);

    const getExportErrorMessage = (error: any) => {
        if (error?.response?.data?.message) return error.response.data.message;
        if (error?.response?.data instanceof ArrayBuffer) {
            try {
                const decoded = JSON.parse(new TextDecoder().decode(error.response.data));
                return decoded.message || 'Error al exportar';
            } catch {
                return 'Error al exportar';
            }
        }
        return error?.message || 'Error al exportar';
    };

    const getFileNameFromContentDisposition = (contentDisposition?: string): string | null => {
        if (!contentDisposition) return null;

        const utf8FileNameMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
        if (utf8FileNameMatch?.[1]) {
            try {
                return decodeURIComponent(utf8FileNameMatch[1].replace(/["']/g, '').trim());
            } catch {
                return utf8FileNameMatch[1].replace(/["']/g, '').trim();
            }
        }

        const fileNameMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
        if (fileNameMatch?.[1]) {
            return fileNameMatch[1].replace(/["']/g, '').trim();
        }

        return null;
    };

    const getImportErrorMessage = (error: any) => {
        const responseData = error?.response?.data;
        if (!responseData) return error?.message || 'Error al importar';

        if (responseData?.data?.errors && Array.isArray(responseData.data.errors)) {
            return responseData.data.errors.join('\n');
        }

        if (responseData?.message) return responseData.message;
        return error?.message || 'Error al importar';
    };

    const handleSelectedRowsChange = useCallback((rows: string[]) => {
        setSelectedProductIds((prevRows) => {
            if (prevRows.length === rows.length && prevRows.every((id, index) => id === rows[index])) {
                return prevRows;
            }
            return rows;
        });
    }, []);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listProducts(filters);
        return response.getResponseData() as ProductsApiResponse;
    }, [filters]));

    useEffect(() => {
        if (!forcedProductTypeId) {
            return;
        }

        const currentProductTypeFilter = filters.filter_filters?.product_type;
        const currentMatchesForcedType =
            Array.isArray(currentProductTypeFilter)
            && currentProductTypeFilter.length === 1
            && currentProductTypeFilter[0] === forcedProductTypeId;

        if (!currentMatchesForcedType) {
            updateFilters({ product_type: [forcedProductTypeId] });
        }

        const currentExcludedProductTypeFilter = filters.filter_filters?.exclude_product_type;
        if (Array.isArray(currentExcludedProductTypeFilter) && currentExcludedProductTypeFilter.length > 0) {
            updateFilters({ exclude_product_type: null });
        }
    }, [forcedProductTypeId, filters.filter_filters?.product_type, filters.filter_filters?.exclude_product_type, updateFilters]);

    useEffect(() => {
        if (!forcedExcludedProductTypeId) {
            return;
        }

        const currentExcludedProductTypeFilter = filters.filter_filters?.exclude_product_type;
        const currentMatchesForcedExcludedType =
            Array.isArray(currentExcludedProductTypeFilter)
            && currentExcludedProductTypeFilter.length === 1
            && currentExcludedProductTypeFilter[0] === forcedExcludedProductTypeId;

        if (!currentMatchesForcedExcludedType) {
            updateFilters({ exclude_product_type: [forcedExcludedProductTypeId] });
        }
    }, [forcedExcludedProductTypeId, filters.filter_filters?.exclude_product_type, updateFilters]);

    useEffect(() => {
        const previousForcedProductTypeId = previousForcedProductTypeIdRef.current;

        if (!forcedProductTypeId && previousForcedProductTypeId) {
            const currentProductTypeFilter = filters.filter_filters?.product_type;
            if (Array.isArray(currentProductTypeFilter) && currentProductTypeFilter.length > 0) {
                updateFilters({ product_type: null });
            }
        }

        previousForcedProductTypeIdRef.current = forcedProductTypeId;
    }, [forcedProductTypeId, filters.filter_filters?.product_type, updateFilters]);

    useEffect(() => {
        const previousForcedExcludedProductTypeId = previousForcedExcludedProductTypeIdRef.current;

        if (!forcedExcludedProductTypeId && previousForcedExcludedProductTypeId) {
            const currentExcludedProductTypeFilter = filters.filter_filters?.exclude_product_type;
            if (Array.isArray(currentExcludedProductTypeFilter) && currentExcludedProductTypeFilter.length > 0) {
                updateFilters({ exclude_product_type: null });
            }
        }

        previousForcedExcludedProductTypeIdRef.current = forcedExcludedProductTypeId;
    }, [forcedExcludedProductTypeId, filters.filter_filters?.exclude_product_type, updateFilters]);

    const toggleStatus = async (id: string, status: boolean) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleProductStatus(id, status)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(status ? "Se ha desactivado el producto" : "Se ha activado el producto");
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };

    const handleDelete = async (id: string) => {
        const response = await (await service.deleteProduct(id)).getResponseData();
        if (response.success) {
            toast.success('Producto eliminado correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    const handleMultiDelete = async (ids: string[]) => {
        const response = await (await service.deleteMultiProducts(ids)).getResponseData();
        if (response.success) {
            toast.success('Productos eliminados correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    /**
     * Order the data to match the column order
     */
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    const handleExportXLSX = async () => {
        setIsLoadingXLSX(true);
        try {
            const response = await service.exportProductsXLSX(filters);
            const fileData = response.getResponseData();
            if (response && fileData) {
                const blob = new Blob([fileData]);
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `productos_${moment().format('DD-MM-YYYY')}.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success('Exportación completada');
            } else {
                toast.error('Error al exportar los productos a Excel');
            }
        } catch (error: any) {
            toast.error(getExportErrorMessage(error));
        } finally {
            setIsLoadingXLSX(false);
        }
    };

    const handleExportXML = async () => {
        setIsLoadingXML(true);
        try {
            const response = await service.exportProductsXML(filters, selectedProductIds);
            const fileData = response.getResponseData();
            const rawResponse = response.getResponse();
            const contentDisposition = rawResponse?.headers?.['content-disposition'] as string | undefined;
            const contentType = rawResponse?.headers?.['content-type'] as string | undefined;
            const fileName = getFileNameFromContentDisposition(contentDisposition) || `acciones_formativas_${moment().format('DD-MM-YYYY')}.zip`;

            if (response && fileData) {
                const blob = new Blob([fileData], { type: contentType || 'application/zip' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success('Exportación completada');
            } else {
                toast.error('Error al exportar los productos a XML');
            }
        } catch (error: any) {
            toast.error(getExportErrorMessage(error));
        } finally {
            setIsLoadingXML(false);
        }
    };

    const handleImportClick = () => {
        setShowImportStartModal(true);
    };

    const closeImportStartModal = () => {
        if (isLoadingImport || isLoadingImportTemplate) return;
        setShowImportStartModal(false);
    };

    const handleDownloadImportTemplate = async () => {
        setIsLoadingImportTemplate(true);
        try {
            const response = await service.downloadProductsImportTemplateXLSX();
            const fileData = response.getResponseData();
            if (response && fileData) {
                const blob = new Blob([fileData]);
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `plantilla_importacion_productos_${moment().format('DD-MM-YYYY')}.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success('Plantilla descargada correctamente');
            } else {
                toast.error('No se ha podido descargar la plantilla');
            }
        } catch (error: any) {
            toast.error(getExportErrorMessage(error));
        } finally {
            setIsLoadingImportTemplate(false);
        }
    };

    const handleSelectImportFile = () => {
        setShowImportStartModal(false);
        importFileInputRef.current?.click();
    };

    const handleImportProducts = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (!fileExtension || !['xlsx', 'xls'].includes(fileExtension)) {
            toast.error('El archivo debe tener formato .xlsx o .xls');
            event.target.value = '';
            return;
        }

        setIsLoadingImport(true);
        try {
            const response = await service.importProductsXLSX(file, 'preview');
            const responseData = response.getResponseData();

            if (responseData?.success) {
                setPendingImportFile(file);
                setImportPreview({
                    rowsWithId: responseData?.data?.rowsWithId ?? 0,
                    rowsWithoutId: responseData?.data?.rowsWithoutId ?? 0,
                    totalRows: responseData?.data?.totalRows ?? 0,
                });
                setShowImportModeModal(true);
            } else {
                const errors = responseData?.data?.errors;
                if (Array.isArray(errors) && errors.length > 0) {
                    toast.error(errors.join('\n'));
                } else {
                    toast.error(responseData?.message || 'No se pudo analizar el archivo de importación');
                }
            }
        } catch (error: any) {
            toast.error(getImportErrorMessage(error));
        } finally {
            setIsLoadingImport(false);
            event.target.value = '';
        }
    };

    const closeImportModeModal = () => {
        setShowImportModeModal(false);
        setPendingImportFile(null);
        setImportPreview(null);
    };

    const executeImportProducts = async (importMode: Exclude<ProductImportMode, 'preview'>) => {
        if (!pendingImportFile) {
            toast.error('No se ha seleccionado ningún archivo para importar');
            return;
        }

        setShowImportModeModal(false);
        setIsLoadingImport(true);
        try {
            const response = await service.importProductsXLSX(pendingImportFile, importMode);
            const responseData = response.getResponseData();

            if (responseData?.success) {
                const createdProducts = responseData?.data?.createdProducts ?? 0;
                const updatedProducts = responseData?.data?.updatedProducts ?? 0;
                const skippedRowsWithId = responseData?.data?.skippedRowsWithId ?? 0;
                const skippedRowsWithoutId = responseData?.data?.skippedRowsWithoutId ?? 0;

                toast.success(
                    `${responseData?.message || 'Importación completada'} ` +
                    `(Creados: ${createdProducts}, Actualizados: ${updatedProducts}, ` +
                    `Saltados con ID: ${skippedRowsWithId}, Saltados sin ID: ${skippedRowsWithoutId})`
                );
                refetch();
            } else {
                const errors = responseData?.data?.errors;
                if (Array.isArray(errors) && errors.length > 0) {
                    toast.error(errors.join('\n'));
                } else {
                    toast.error(responseData?.message || 'No se pudo completar la importación');
                }
            }
        } catch (error: any) {
            toast.error(getImportErrorMessage(error));
        } finally {
            setIsLoadingImport(false);
            setPendingImportFile(null);
            setImportPreview(null);
        }
    };

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'products-table'}
                    largeTable
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    overrideColumns={[
                        {
                            key: 'productType',
                            render: (row: any) => { return row.row.original.productType?.name },
                        },
                        {
                            key: 'amount',
                            render: (row: any) => { return row.row.original.amount ? fixNumber(row.row.original.amount) + '€' : 'N/A' },
                        },
                        {
                            key: 'weeklyHours',
                            render: (row: any) => { return row.row.original.weeklyHours ? row.row.original.weeklyHours + ' h' : 'N/A' },
                        },
                        {
                            key: 'complementsNumber',
                            render: (row: any) => { return row.row.original.complementsNumber ? row.row.original.complementsNumber + '' : '0' },
                        },
                        {
                            key: 'active',
                            render: (row: any) => {
                                return (
                                    <Switch
                                        id={row.row.original.id}
                                        checked={row.row.original.active}
                                        onChange={() => toggleStatus(row.row.original.id, row.row.original.active)}
                                        switchKnobClassName='bg-secondary'
                                    />
                                );
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'Edit',
                            permissions: { group: 'products', permission: 'edit_products' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar producto'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link to={`${menuRoutes.products.path}/${row.id}/edit`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700"
                                            >
                                                <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'products', permission: 'delete_products' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar producto`}
                                                description={`¿Estás seguro de que deseas eliminar el ${row.name}?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    multiDeletePermission={{ group: 'products', action: 'delete_products' }}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as ProductsApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                    onSelectedRowsChange={handleSelectedRowsChange}
                >
                    <div className="flex items-center gap-2">
                        {canImportProductsExcel && (
                            <>
                                <input
                                    ref={importFileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    className="hidden"
                                    onChange={handleImportProducts}
                                />
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleImportClick}
                                    isLoading={isLoadingImport || isLoadingImportTemplate}
                                    disabled={isLoadingImport || isLoadingImportTemplate}
                                    className="p-3 text-nowrap"
                                >
                                    Importar XLSX
                                </Button>
                            </>
                        )}
                        {canExportProductsExcel && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleExportXLSX}
                                isLoading={isLoadingXLSX}
                                disabled={isLoadingXLSX}
                                className="p-3 text-nowrap"
                            >
                                Exportar XLSX
                            </Button>
                        )}
                        {userCan('admin_products', 'products') && userCan('export_products', 'products') && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleExportXML}
                                isLoading={isLoadingXML}
                                disabled={isLoadingXML}
                                className="p-3 text-nowrap"
                            >
                                Exportar XML
                            </Button>
                        )}
                    </div>
                </CustomTable>
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Productos'}>
                <ProductsFilters
                    filters={filters}
                    updateFilters={updateFilters}
                    resetFilters={resetFilters}
                    showProductTypeFilter={!hideProductTypeFilter}
                />
            </FilterDrawerView>

            <Modal
                isOpen={showImportStartModal}
                onClose={closeImportStartModal}
                size="md"
                overlayClassName='backdrop-blur'
                containerClassName='!shadow-2xl'
                className='z-[999] [&_.pointer-events-none]:overflow-visible'
            >
                <div className="m-auto px-7 pt-6 pb-8">
                    <div className="mb-5 flex items-center justify-between">
                        <h4 className="text-xl font-semibold">Importar Productos</h4>
                        <ActionIcon
                            size='sm'
                            variant='text'
                            onClick={closeImportStartModal}
                            disabled={isLoadingImport || isLoadingImportTemplate}
                        >
                            <IoClose className='h-auto w-6' strokeWidth={1.8} />
                        </ActionIcon>
                    </div>

                    <div className="rounded-md border border-[#b4d4f5] bg-[#e9f3fe] p-4">
                        <div className="flex items-center gap-2 text-[#1e5799]">
                            <IoInformationCircleOutline size={20} />
                            <p className="font-semibold">Información importante</p>
                        </div>
                        <p className="mt-3 text-sm text-[#2f5f8f]">
                            Importa productos usando siempre la misma estructura de columnas. Si el archivo contiene
                            filas con formato inválido, se mostrará un aviso detallado antes de guardar cambios.
                        </p>
                        <p className="mt-3 text-sm text-[#2f5f8f]">
                            Descarga la plantilla oficial para completar los datos con el formato correcto.
                        </p>
                        <Button
                            variant="text"
                            className="mt-2 !px-0 !text-[#1e5799] underline hover:!text-[#164678]"
                            onClick={handleDownloadImportTemplate}
                            isLoading={isLoadingImportTemplate}
                            disabled={isLoadingImport || isLoadingImportTemplate}
                        >
                            Descargar plantilla
                        </Button>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                        <Button
                            variant="outline"
                            onClick={closeImportStartModal}
                            disabled={isLoadingImport || isLoadingImportTemplate}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSelectImportFile}
                            disabled={isLoadingImport || isLoadingImportTemplate}
                        >
                            Seleccionar archivo
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal isOpen={showImportModeModal} onClose={closeImportModeModal} size="md">
                <div className="p-6">
                    <h4 className="text-xl font-semibold text-center">Importación de Productos</h4>
                    <p className="text-sm text-gray-500 mt-3 text-center">
                        El Excel se ha analizado correctamente. Se han detectado{' '}
                        <strong>{importPreview?.rowsWithId ?? 0}</strong> filas con ID y{' '}
                        <strong>{importPreview?.rowsWithoutId ?? 0}</strong> filas sin ID.
                    </p>
                    <p className="text-sm text-gray-500 mt-2 text-center">
                        Selecciona cómo quieres procesar la importación.
                    </p>
                    <div className="mt-5 flex flex-col gap-3">
                        <Button
                            onClick={() => executeImportProducts('upsert')}
                            disabled={isLoadingImport || !importPreview?.totalRows}
                            isLoading={isLoadingImport}
                        >
                            Actualizar filas existentes e introducir las nuevas
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => executeImportProducts('create_only')}
                            disabled={isLoadingImport || !importPreview?.rowsWithoutId}
                        >
                            Únicamente introducir nuevas filas
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => executeImportProducts('update_only')}
                            disabled={isLoadingImport || !importPreview?.rowsWithId}
                        >
                            Únicamente actualizar las existentes
                        </Button>
                        <Button
                            variant="outline"
                            onClick={closeImportModeModal}
                            disabled={isLoadingImport}
                            className="!border-red-200 !text-red-600 hover:!border-red-400"
                        >
                            Cancelar proceso
                        </Button>
                    </div>
                </div>
            </Modal>
        </Page>
    );
};

export default ProductsList;
